import type { ModalNameType, ModalPayload } from '@/modals/components/ModalProvider';
import { ensureCurrentEntryHasInAppNavigationIndex, IN_APP_NAVIGATION_STATE_KEY } from '@/utils/inAppNavigation';
import { MODAL_DATA_PARAM, MODAL_HISTORY_STEPS_STATE, MODAL_PARAM, PUSHED_MODAL_HISTORY_STATE } from '../consts';

type OpenModalOptions = {
  history?: 'push' | 'replace';
};

let modalNavigationVersion = 0;

export const getModalNavigationVersion = () => modalNavigationVersion;

type OpenModalArguments<TName extends ModalNameType>
  = ModalPayload<TName> extends void
    ? [modalData?: undefined, options?: OpenModalOptions]
    : undefined extends ModalPayload<TName>
      ? [modalData?: Exclude<ModalPayload<TName>, undefined>, options?: OpenModalOptions]
      : [modalData: ModalPayload<TName>, options?: OpenModalOptions];

const getModalUrl = (
  modalName: ModalNameType,
  modalData: unknown,
): string => {
  const params = new URLSearchParams();
  params.set(MODAL_PARAM, modalName);
  if (modalData != null) {
    params.set(MODAL_DATA_PARAM, JSON.stringify(modalData));
  }
  return `?${params.toString()}`;
};

const getModalHistoryState = (options?: { preservePushedModalEntry?: boolean; preserveSteps?: boolean }): Record<string, unknown> => {
  const state = window.history.state;
  if (!state || typeof state !== 'object') {
    return {};
  }

  const historyState: Record<string, unknown> = {};
  const inAppIndex = (state as Record<string, unknown>)[IN_APP_NAVIGATION_STATE_KEY];
  if (typeof inAppIndex === 'number') {
    historyState[IN_APP_NAVIGATION_STATE_KEY] = inAppIndex;
  }

  const steps = options?.preserveSteps ? (state as Record<string, unknown>)[MODAL_HISTORY_STEPS_STATE] : undefined;
  if (typeof steps === 'number' && steps > 0) {
    historyState[MODAL_HISTORY_STEPS_STATE] = steps;
  }

  if ((options?.preservePushedModalEntry || historyState[MODAL_HISTORY_STEPS_STATE]) && (state as Record<string, unknown>)[PUSHED_MODAL_HISTORY_STATE] === true) {
    historyState[PUSHED_MODAL_HISTORY_STATE] = true;
  }

  return historyState;
};

const hasActiveModalParam = (): boolean => {
  return new URLSearchParams(window.location.search).has(MODAL_PARAM);
};

function openModal<TName extends ModalNameType>(
  modalName: TName,
  ...args: OpenModalArguments<TName>
): void;
function openModal(
  modalName: ModalNameType,
  modalData?: unknown,
  options?: OpenModalOptions,
): void {
  ensureCurrentEntryHasInAppNavigationIndex();

  const url = getModalUrl(modalName, modalData);
  const shouldReplaceActiveModal = !options?.history && hasActiveModalParam();
  modalNavigationVersion += 1;

  if (options?.history === 'replace' || shouldReplaceActiveModal) {
    window.history.replaceState(getModalHistoryState({ preservePushedModalEntry: shouldReplaceActiveModal, preserveSteps: true }), '', url);
    return;
  }

  window.history.pushState({ ...getModalHistoryState(), [PUSHED_MODAL_HISTORY_STATE]: true }, '', url);
}

export default openModal;
